import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useOrganization } from './useOrganization';

export interface KanbanColumnConfig {
    slug: string;      // immutable — stored as missions.status value
    label: string;     // display name (editable)
    emoji: string;
    color: string;
    position: number;
}

export const COLOR_STYLES: Record<string, string> = {
    slate:   'bg-slate-50 border-t-4 border-slate-300',
    blue:    'bg-blue-50 border-t-4 border-blue-500',
    amber:   'bg-amber-50 border-t-4 border-amber-500',
    purple:  'bg-purple-50 border-t-4 border-purple-500',
    emerald: 'bg-emerald-50 border-t-4 border-emerald-500',
    rose:    'bg-rose-50 border-t-4 border-rose-500',
    indigo:  'bg-indigo-50 border-t-4 border-indigo-500',
    orange:  'bg-orange-50 border-t-4 border-orange-500',
};

export const COLOR_DOTS: Record<string, string> = {
    slate:   'bg-slate-400',
    blue:    'bg-blue-500',
    amber:   'bg-amber-500',
    purple:  'bg-purple-500',
    emerald: 'bg-emerald-500',
    rose:    'bg-rose-500',
    indigo:  'bg-indigo-500',
    orange:  'bg-orange-500',
};

export const DEFAULT_COLUMNS: KanbanColumnConfig[] = [
    { slug: 'Prospection',  label: 'Prospection',  emoji: '🔮', color: 'slate',   position: 0 },
    { slug: 'Qualification', label: 'Qualification', emoji: '🧐', color: 'blue',   position: 1 },
    { slug: 'Proposition',  label: 'Proposition',  emoji: '📝', color: 'amber',   position: 2 },
    { slug: 'Negociation',  label: 'Négociation',  emoji: '🤝', color: 'purple',  position: 3 },
    { slug: 'Cloture',      label: 'Clôture',      emoji: '✅', color: 'emerald', position: 4 },
];

export const useKanbanColumns = () => {
    const { org } = useOrganization();
    const [columns, setColumns] = useState<KanbanColumnConfig[]>(DEFAULT_COLUMNS);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (!org?.id) return;
        const load = async () => {
            const { data } = await supabase
                .from('organizations')
                .select('kanban_config')
                .eq('id', org.id)
                .single();
            const cols = (data as any)?.kanban_config?.columns;
            if (Array.isArray(cols) && cols.length > 0) {
                setColumns([...cols].sort((a, b) => a.position - b.position));
            } else {
                setColumns(DEFAULT_COLUMNS);
            }
            setIsLoading(false);
        };
        load();
    }, [org?.id]);

    const save = useCallback(async (newColumns: KanbanColumnConfig[]) => {
        if (!org?.id) return;
        setIsSaving(true);
        await supabase
            .from('organizations')
            .update({ kanban_config: { columns: newColumns } } as any)
            .eq('id', org.id);
        setColumns(newColumns);
        setIsSaving(false);
    }, [org?.id]);

    const countMissionsForSlug = useCallback(async (slug: string): Promise<number> => {
        const { count } = await supabase
            .from('missions')
            .select('id', { count: 'exact', head: true })
            .eq('status', slug);
        return count ?? 0;
    }, []);

    const addColumn = useCallback(async (label: string, emoji: string, color: string): Promise<KanbanColumnConfig> => {
        const base = label
            .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
            .replace(/[^a-zA-Z0-9]/g, '_')
            .replace(/_+/g, '_')
            .replace(/^_|_$/g, '')
            || 'Colonne';
        let finalSlug = base;
        let i = 2;
        while (columns.some(c => c.slug === finalSlug)) {
            finalSlug = `${base}_${i++}`;
        }
        const newCol: KanbanColumnConfig = {
            slug: finalSlug,
            label,
            emoji: emoji || '🔵',
            color,
            position: columns.length,
        };
        await save([...columns, newCol]);
        return newCol;
    }, [columns, save]);

    const updateColumn = useCallback(async (slug: string, updates: Partial<Pick<KanbanColumnConfig, 'label' | 'emoji' | 'color'>>) => {
        const newCols = columns.map(c => c.slug === slug ? { ...c, ...updates } : c);
        await save(newCols);
    }, [columns, save]);

    const deleteColumn = useCallback(async (slug: string): Promise<{ blocked: boolean; count: number }> => {
        const count = await countMissionsForSlug(slug);
        if (count > 0) return { blocked: true, count };
        const newCols = columns
            .filter(c => c.slug !== slug)
            .map((c, i) => ({ ...c, position: i }));
        await save(newCols);
        return { blocked: false, count: 0 };
    }, [columns, countMissionsForSlug, save]);

    const moveColumn = useCallback(async (slug: string, direction: 'up' | 'down') => {
        const idx = columns.findIndex(c => c.slug === slug);
        if (idx < 0) return;
        const newIdx = direction === 'up' ? idx - 1 : idx + 1;
        if (newIdx < 0 || newIdx >= columns.length) return;
        const newCols = [...columns];
        [newCols[idx], newCols[newIdx]] = [newCols[newIdx], newCols[idx]];
        await save(newCols.map((c, i) => ({ ...c, position: i })));
    }, [columns, save]);

    return { columns, isLoading, isSaving, addColumn, updateColumn, deleteColumn, moveColumn };
};
